import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Bell, AlertTriangle, Info, CheckCircle, Clock, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';

interface Notification {
  id: string;
  timestamp: string;
  severity: 'critical' | 'warning' | 'info';
  payer: string;
  title: string;
  description: string;
  affectedSurgeons: string[];
  read: boolean;
}

const mockNotifications: Notification[] = [
  {
    id: '1', timestamp: '2026-02-10T08:30:00Z', severity: 'critical', payer: 'UnitedHealthcare',
    title: 'Prior Auth Requirement Added',
    description: 'UnitedHealthcare now requires prior authorization for CPT 29826 (arthroscopic subacromial decompression) effective March 1, 2026. All pending cases must be resubmitted.',
    affectedSurgeons: ['Dr. Larkin', 'Dr. Chen'], read: false,
  },
  {
    id: '2', timestamp: '2026-02-09T14:15:00Z', severity: 'warning', payer: 'BCBS Ohio',
    title: 'Modifier Documentation Rule Change',
    description: 'BCBS Ohio updated modifier -59 documentation requirements for multi-procedure knee arthroscopy. Distinct operative report sections now mandatory.',
    affectedSurgeons: ['Dr. Larkin', 'Dr. Torres', 'Dr. Chen'], read: false,
  },
  {
    id: '3', timestamp: '2026-02-08T09:00:00Z', severity: 'info', payer: 'Aetna',
    title: 'Fee Schedule Update — Q2 2026',
    description: 'Aetna published updated fee schedule for orthopedic procedures. No material changes to TKA/THA reimbursement rates.',
    affectedSurgeons: ['Dr. Larkin', 'Dr. Wright'], read: true,
  },
  {
    id: '4', timestamp: '2026-02-07T11:45:00Z', severity: 'warning', payer: 'BWC Ohio',
    title: 'Timely Filing Window Shortened',
    description: 'BWC Ohio reduced timely filing window from 365 days to 180 days for all surgical claims effective April 1, 2026.',
    affectedSurgeons: ['Dr. Torres'], read: true,
  },
  {
    id: '5', timestamp: '2026-02-06T16:20:00Z', severity: 'info', payer: 'UnitedHealthcare',
    title: 'Portal Maintenance Window',
    description: 'UnitedHealthcare portal scheduled for maintenance Feb 15, 12am–4am EST. Submissions will queue automatically.',
    affectedSurgeons: [], read: true,
  },
];

const severityConfig = {
  critical: { icon: AlertTriangle, color: 'text-destructive', bg: 'bg-destructive/10 border-destructive/20', label: 'Critical' },
  warning: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800', label: 'Warning' },
  info: { icon: Info, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800', label: 'Info' },
};

export function CommunicationHub() {
  const [notifications, setNotifications] = useState(mockNotifications);
  const unreadCount = notifications.filter(n => !n.read).length;

  const markRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              Payer Notification Center
              {unreadCount > 0 && (
                <Badge variant="destructive" className="text-[10px] ml-1">{unreadCount} new</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Payer rule changes routed to Admin — surgeons stay focused on clinical work
            </CardDescription>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={markAllRead}>
              <Eye className="w-3 h-3" />
              Mark all read
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {notifications.map(n => {
          const cfg = severityConfig[n.severity];
          const Icon = cfg.icon;
          return (
            <div
              key={n.id}
              className={cn(
                'border rounded-lg p-3 transition-colors',
                cfg.bg,
                !n.read && 'ring-1 ring-primary/20'
              )}
            >
              <div className="flex items-start gap-3">
                <Icon className={cn('w-4 h-4 mt-0.5 shrink-0', cfg.color)} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{n.title}</span>
                    <Badge variant="outline" className="text-[10px]">{n.payer}</Badge>
                    {!n.read && <Badge className="text-[10px] bg-primary">New</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{n.description}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" />
                      {new Date(n.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {n.affectedSurgeons.length > 0 && (
                      <span className="text-[10px] text-muted-foreground">
                        Affects: {n.affectedSurgeons.join(', ')}
                      </span>
                    )}
                  </div>
                </div>
                {!n.read && (
                  <Button variant="ghost" size="sm" className="text-[10px] shrink-0" onClick={() => markRead(n.id)}>
                    <CheckCircle className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
